import { inject, injectable } from 'inversify'
import { action, makeObservable, observable } from 'mobx'
import { Types } from '@core/ioc/types'
import type { LocalStorageGateway } from '@core/services/local-storage.gateway'

export type SidebarMode = 'closed' | 'expanded'

const SIDEBAR_MODES: SidebarMode[] = ['closed', 'expanded']
const STORAGE_KEY = 'sidebar-mode'

/** Source of truth for the app shell's own chrome state — currently just the sidebar's open/closed mode. */
@injectable()
export class ShellRepository {
  // Always resolves to something — see the note in ThemeRepository.
  sidebarModePm: SidebarMode = 'closed'

  @inject(Types.ILocalStorageGateway)
  private localStorageGateway!: LocalStorageGateway

  constructor() {
    makeObservable(this, { sidebarModePm: observable })
  }

  /**
   * The fallback is passed in rather than baked in: what "no preference yet"
   * should mean depends on the viewport, which is presentation policy and lives
   * in ShellPresenter. This half only knows how to read and write the value.
   */
  restore = action((fallback: SidebarMode) => {
    const stored = this.localStorageGateway.getValue(STORAGE_KEY)
    this.sidebarModePm =
      typeof stored === 'string' && SIDEBAR_MODES.includes(stored as SidebarMode) ? (stored as SidebarMode) : fallback
  })

  setSidebarMode = action((mode: SidebarMode) => {
    this.sidebarModePm = mode
    this.localStorageGateway.setValue(STORAGE_KEY, mode)
  })
}
